import type { Miscellaneous, Tokens, BorderSize, Paths, Palette, ThemeOption, ThemeOptionVariant } from '@dhl-ui/theme'
import { hasOwnProperty } from '../../helpers'
import { createBorderSize, borderRadius, type BorderStyle } from './border'
import { createHoverOrActiveState } from './hover'
import { createFocusVisibleState } from './focus'

const resolvePaletteColour = (palette: Palette, path: Paths<Palette>) =>
  path.split('.').reduce((o: Palette, key: Paths<Palette>) => (o && hasOwnProperty(o, key) ? o[key] : null), palette)

type Props = Pick<ThemeOption, 'disableActiveState'> &
  Pick<Tokens, 'palette'> & {
    variant: ThemeOptionVariant
  }

/**
 * this is our place for some helpers to accommodate styling (e.g. border)
 * for the borderStyle, you can use the path of the Palette to select the colour:
 * e.g: borderStyle('grey.300') | borderStyle('primary.main')
 *
 * @example: you can find this in the misc section of the theme, i.e: ({ theme: { misc: { borderStyle } } }) => ({ border: borderStyle('grey.300') })
 * size is in line with BorderSize, and will be applied to spacing;
 */
export const miscellaneous = ({ palette, disableActiveState, variant = 'desktop' }: Props): Miscellaneous => {
  const hoverOrActiveState = createHoverOrActiveState(disableActiveState)
  const borderSize = createBorderSize(variant)

  return {
    borderStyle: (colour: Paths<Palette>, size: BorderSize = 1, style: BorderStyle = 'solid') =>
      `${borderSize(size)} ${style} ${resolvePaletteColour(palette, colour)}`,
    borderSize,
    borderRadius,
    createHoverOrActiveState: hoverOrActiveState,
    createFocusVisibleState,
  }
}
